// components/settings/Notifications.js
import React, { useState, useEffect } from "react";
import NotificationSwitch from "./NotificationItem";
import { API_BASE } from "../../Utils/constant";
import { AUTH_TOKEN } from "../../constants/localStorageKeys";
import { retrieve } from "../../helpers/StorageHelper";

const Notifications = () => {
  const [notificationStates, setNotificationStates] = useState(null);

  const [originalStates, setOriginalStates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const getToken = () => retrieve(AUTH_TOKEN);

  const showErrorAndRedirect = () => {
    setError("Authentication token not found. Please log in again.");
    setTimeout(() => {
      // window.location.href = "/login";
    }, 2000);
  };

  const fetchNotificationSettings = async () => {
    setIsLoading(true);
    setError(null);
    const token = getToken();

    if (!token) {
      showErrorAndRedirect();
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/settings/notifications`,

        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();

      const updatedStates = {
        checkInReminder: data.data["Check In Reminder"].check_in_reminder,
        newMessage: data.data["New Message Added"].new_message_added,
        newVideo: data.data["New Video Added"].new_video_added,
        newReply: data.data["New Reply To Message"].new_reply_to_message,
      };

      updatedStates.all =
        updatedStates.checkInReminder &&
        updatedStates.newMessage &&
        updatedStates.newVideo &&
        updatedStates.newReply;

      setNotificationStates(updatedStates);
      setOriginalStates({ ...updatedStates });
      setHasChanges(false);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to load notification settings.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNotification = (key) => {
    const updatedStates = { ...notificationStates };

    if (key === "all") {
      const newAllState = !updatedStates.all;
      updatedStates.all = newAllState;
      updatedStates.checkInReminder = newAllState;
      updatedStates.newMessage = newAllState;
      updatedStates.newVideo = newAllState;
      updatedStates.newReply = newAllState;
    } else {
      updatedStates[key] = !updatedStates[key];
      updatedStates.all =
        updatedStates.checkInReminder &&
        updatedStates.newMessage &&
        updatedStates.newVideo &&
        updatedStates.newReply;
    }

    setNotificationStates(updatedStates);
    submitNotificationChanges(updatedStates); // <-- save immediately
  };

  const submitNotificationChanges = async (newStates) => {
    // setIsLoading(true);
    setError(null);
    const token = getToken();

    if (!token) {
      showErrorAndRedirect();
      setIsLoading(false);
      return;
    }

    const payload = {
      check_in_reminder: newStates.checkInReminder,
      new_message_added: newStates.newMessage,
      new_video_added: newStates.newVideo,
      new_reply_to_message: newStates.newReply,
    };

    try {
      const response = await fetch(`${API_BASE}/settings/notifications`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      setOriginalStates({ ...newStates });
    } catch (error) {
      console.error("Update error:", error);
      setError("Failed to update notification settings.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="profile-container">
        <h1 className="setting-header text-heading-h3 mb-3 mb-md-4">
          Notifications
        </h1>

        {isLoading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="notifications-container">
            <NotificationSwitch
              label="All"
              checked={notificationStates.all}
              onToggle={() => toggleNotification("all")}
            />
            <hr className="accordion-horizontal-line m-0" />

            <NotificationSwitch
              label="Check-in reminder"
              checked={notificationStates.checkInReminder}
              onToggle={() => toggleNotification("checkInReminder")}
              disabled={isLoading}
            />
            <NotificationSwitch
              label="New message added"
              checked={notificationStates.newMessage}
              onToggle={() => toggleNotification("newMessage")}
              disabled={isLoading}
            />
            <NotificationSwitch
              label="New video added"
              checked={notificationStates.newVideo}
              onToggle={() => toggleNotification("newVideo")}
              disabled={isLoading}
            />
            <NotificationSwitch
              label="New reply to message"
              checked={notificationStates.newReply}
              onToggle={() => toggleNotification("newReply")}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
